import LiveAttendance from "@/components/LiveAttendance";
import { useCallback, useEffect, useRef, useState } from "react";
import { DeviceHealthCompact } from "@/components/DeviceHealth";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { AccountAvatar } from "@/components/AccountAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { ApprovalsSummaryCard } from "@/components/ApprovalsSummaryCard";
import { useNavigate } from "react-router-dom";
import {
  Banknote,
  CalendarDays,
  Users,
  Scissors,
  Wallet,
  TrendingUp,
  Loader2,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  ClipboardList,
  Package,
  UserCog,
  Check,
  X,
  Activity,
  Bell,
  Unlock,
  Gift,
  Trash2,
  Clock,
  ShieldCheck,
  Pencil,
  Inbox,
  ClipboardCheck,
  AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Area,
  AreaChart,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { api, errMsg } from "@/lib/api";
import { rp } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PRESETS = [
  { k: "1", label: "1M" },
  { k: "3", label: "3M" },
  { k: "6", label: "6M" },
  { k: "12", label: "1Y" },
];

const NAV = [
  { k: "payroll", to: "/payroll", lblKey: "nav.payroll", dKey: "dash.navPayrollDesc", icon: Banknote },
  { k: "absensi", to: "/absensi", lblKey: "nav.attendance", dKey: "dash.navAbsensiDesc", icon: ClipboardList },
  { k: "jarit", to: "/jarit", lblKey: "nav.tailors", dKey: "dash.navJaritDesc", icon: Scissors },
  { k: "karyawan", to: "/karyawan", lblKey: "nav.employees", dKey: "dash.navKaryawanDesc", icon: Users },
  { k: "titip", to: "/saldo-titip", lblKey: "nav.savings", dKey: "dash.navTitipDesc", icon: Wallet },
  { k: "account", to: "/account", lblKey: "nav.account", dKey: "dash.navAccountDesc", icon: UserCog },
];

const rpShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(v);
};

const titleCase = (s) => (s || "").toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase());

const VerifiedBadge = ({ className = "h-[14px] w-[14px]" }) => (
  <img src="/verified-badge.png" alt="Verified" className={`ml-0.5 inline-block shrink-0 align-text-bottom ${className}`} />
);

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const ACT_META = {
  jarit: { color: "#AF52DE", bg: "#AF52DE14", Icon: Scissors },
  kasbon: { color: "#FF3B30", bg: "#FF3B3014", Icon: Wallet },
  bonus: { color: "#128C3E", bg: "#128C3E14", Icon: Gift },
  recap: { color: "#0071E3", bg: "#0071E314", Icon: ClipboardList },
  attendance: { color: "#FF9500", bg: "#FF950014", Icon: CalendarDays },
  delete: { color: "#FF3B30", bg: "#FF3B3014", Icon: Trash2 },
  request: { color: "#8A5A00", bg: "#8A5A0014", Icon: Clock },
  approve: { color: "#128C3E", bg: "#128C3E14", Icon: ShieldCheck },
  export: { color: "#5856D6", bg: "#5856D614", Icon: Download },
  titip: { color: "#00A0A0", bg: "#00A0A014", Icon: Wallet },
  edit: { color: "#8E8E93", bg: "#8E8E9314", Icon: Pencil },
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const Delta = ({ cur, prev, invert }) => {
  if (prev == null || prev === 0) return null;
  const pct = ((Number(cur) - Number(prev)) / Math.abs(Number(prev))) * 100;
  if (!isFinite(pct)) return null;
  const up = pct >= 0;
  const good = invert ? !up : up;
  const color = Math.abs(pct) < 0.5 ? "#8E8E93" : good ? "#128C3E" : "#FF3B30";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="mt-0.5 flex items-center gap-0.5 text-[11.5px] font-semibold" style={{ color }}>
      <Icon className="h-3 w-3" /> {Math.abs(pct).toFixed(0)}%
      <span className="ml-0.5 font-normal text-[#B0B0B8]">vs prev</span>
    </span>
  );
};

const StatCard = ({ testid, icon: Icon, label, value, accent, cur, prev, invert }) => (
  <div data-testid={testid} className="liquid-glass-card flex items-start gap-2.5 p-3.5 sm:gap-4 sm:p-5">
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl sm:h-11 sm:w-11 sm:rounded-2xl" style={{ background: `${accent}14`, color: accent }}>
      <Icon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="label truncate">{label}</p>
      <p className="num mt-0.5 break-words text-[13px] font-bold leading-tight tracking-tight tabular-nums text-[#1D1D1F] sm:text-[22px]">{value}</p>
      <Delta cur={cur} prev={prev} invert={invert} />
    </div>
  </div>
);

const RankList = ({ testid, title, icon: Icon, rows, valueOf, fmt, accent, unit, denom, onSelect, itemMode }) => {
  const { t } = useT();
  const max = Math.max(1, ...rows.map((r) => valueOf(r)));
  return (
    <div data-testid={testid} className="liquid-glass-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-[13.5px] text-[#8E8E93]">{t("dash.noRange")}</p>
      ) : (
        <ol className="space-y-2.5">
          {rows.map((r, i) => {
            const v = valueOf(r);
            const clickable = !!(onSelect && (r.employee_id || itemMode));
            return (
              <li key={r.employee_id || r.nama_barang || i}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && onSelect(r)}
                  data-testid={r.employee_id ? TID.home.empRow(r.employee_id) : undefined}
                  className={`flex w-full items-center gap-3 rounded-lg py-1 text-left ${clickable ? "transition-colors hover:bg-black/[0.03]" : "cursor-default"}`}
                >
                  <span className="w-5 shrink-0 text-[12px] font-semibold tabular-nums text-[#B0B0B8]">{i + 1}</span>
                  {r.employee_id && <EmployeeAvatar id={r.employee_id} nama={r.nama} hasPhoto={r.has_photo} size={30} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`truncate text-[13.5px] font-medium ${clickable ? "group-hover:underline" : ""}`}>{r.nama || r.nama_barang}</span>
                      <span className="num shrink-0 text-[13px] font-semibold" style={{ color: accent }}>
                        {fmt(v)}{denom != null ? <span className="text-[#8E8E93]"> / {denom}</span> : null}
                        {unit ? <span className="ml-0.5 text-[11px] font-normal text-[#8E8E93]">{unit}</span> : null}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/[0.05]">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(v / max) * 100}%`, background: accent }} />
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

// ---- semicircle gauge geometry (SVG, over-the-top arc) ----
const _pol = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
};
const _arc = (cx, cy, r, startDeg, endDeg) => {
  const [sx, sy] = _pol(cx, cy, r, startDeg);
  const [ex, ey] = _pol(cx, cy, r, endDeg);
  return `M ${sx} ${sy} A ${r} ${r} 0 0 1 ${ex} ${ey}`;
};

// Attendance Pulse — interactive semicircle gauge + month ribbon (pick any month)
const AttendanceRateChart = ({ trend }) => {
  const { t } = useT();
  const data = trend || [];
  const n = data.length;
  const [sel, setSel] = useState(null);
  const defIdx = (() => {
    for (let i = n - 1; i >= 0; i--) if ((data[i].att_rate || 0) > 0) return i;
    return Math.max(0, n - 1);
  })();
  const idx = sel == null || sel >= n ? defIdx : sel;
  const cur = data[idx] || { att_rate: 0, label: "" };
  const rate = Math.max(0, Math.min(100, Math.round(Number(cur.att_rate) || 0)));
  const avg = n ? Math.round(data.reduce((a, b) => a + (b.att_rate || 0), 0) / n) : 0;
  const peak = n ? data.reduce((mi, r, i, arr) => (r.att_rate > arr[mi].att_rate ? i : mi), 0) : 0;
  const tone = rate >= 85
    ? { a: "#34C759", b: "#0E8A46", lbl: "dash.attGood" }
    : rate >= 60
    ? { a: "#FFB23E", b: "#E8820E", lbl: "dash.attOk" }
    : { a: "#FF6B61", b: "#C0362C", lbl: "dash.attLow" };
  const CX = 110, CY = 105, RAD = 90;
  const GAUGE_LEN = Math.PI * RAD;
  return (
    <div data-testid="home-attendance-rate" className="liquid-glass-card flex h-full flex-col p-5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[#FF9500]" />
          <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t("dash.attRate")}</h3>
        </div>
        <span className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: `${tone.a}24`, color: tone.b }}>{t(tone.lbl)}</span>
      </div>
      <p className="mb-2 text-[12.5px] text-[#8E8E93]">{t("dash.attRateDesc")}</p>
      {n === 0 ? (
        <div className="grid flex-1 place-items-center text-[13px] text-[#8E8E93]">{t("dash.attNoData")}</div>
      ) : (
        <>
          <div className="relative mx-auto w-full max-w-[280px]">
            <svg viewBox="0 0 220 118" className="w-full">
              <defs>
                <linearGradient id="apGauge" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={tone.a} /><stop offset="100%" stopColor={tone.b} />
                </linearGradient>
              </defs>
              <path d={_arc(CX, CY, RAD, 180, 0)} fill="none" stroke="#0000000D" strokeWidth="15" strokeLinecap="round" />
              <path d={_arc(CX, CY, RAD, 180, 0)} fill="none" stroke="url(#apGauge)" strokeWidth="15" strokeLinecap="round"
                strokeDasharray={GAUGE_LEN} strokeDashoffset={GAUGE_LEN * (1 - rate / 100)}
                style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)" }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-1.5">
              <span className="num text-[38px] font-bold leading-none tracking-tight" style={{ color: tone.b }}>
                {rate}<span className="text-[18px] align-top">%</span>
              </span>
              <span className="mt-1 text-[12px] font-medium text-[#6E6E73]">{cur.label}</span>
            </div>
          </div>
          <div className="mb-3 mt-2 flex flex-wrap items-center justify-center gap-2">
            <span className="flex items-center gap-1.5 rounded-full bg-black/[0.04] px-2.5 py-1 text-[11.5px] font-medium text-[#6E6E73]">{t("dash.attAvg")} <b className="num text-[#1D1D1F]">{avg}%</b></span>
            {n > 1 && <span className="flex items-center gap-1 rounded-full bg-[#FFF4E0] px-2.5 py-1 text-[11.5px] font-medium text-[#B26A00]">{t("dash.attPeak")} <b className="num">{Math.round(data[peak].att_rate)}%</b> · {data[peak].label}</span>}
          </div>
          <div className="mt-auto flex items-end justify-between gap-1.5">
            {data.map((d, i) => {
              const h = 12 + (Math.max(0, Math.min(100, d.att_rate)) / 100) * 48;
              const active = i === idx;
              return (
                <button
                  key={i}
                  data-testid={`att-month-${i}`}
                  onClick={() => setSel(i)}
                  onMouseEnter={() => setSel(i)}
                  className="flex flex-1 flex-col items-center gap-1.5"
                  title={`${d.label}: ${Math.round(d.att_rate)}%`}
                >
                  <span className="flex w-full items-end justify-center overflow-hidden rounded-full bg-black/[0.04]" style={{ height: 60 }}>
                    <span className="block w-full rounded-full transition-all duration-300"
                      style={{ height: h, background: active ? `linear-gradient(180deg,${tone.a},${tone.b})` : "#BFC4CE" }} />
                  </span>
                  <span className={`max-w-full truncate text-[9.5px] font-semibold ${active ? "" : "text-[#B0B0B8]"}`} style={active ? { color: tone.b } : undefined}>
                    {(d.label || "").split(" ")[0]}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Monthly Flow — salary gradient area layered with cash-advance bars (ComposedChart)
const MonthlyFlowChart = ({ trend, hideSalary }) => {
  const { t } = useT();
  const showSalary = !hideSalary;
  const data = trend || [];
  const single = data.length < 2;
  const totGaji = data.reduce((a, b) => a + (b.total_gaji || 0), 0);
  const totKasbon = data.reduce((a, b) => a + (b.total_kasbon || 0), 0);
  const denom = totGaji + totKasbon || 1;
  const gajiPct = (totGaji / denom) * 100;
  return (
    <div data-testid={TID.home.trend} className="liquid-glass-card h-full p-5">
      <div className="mb-1 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-[#0071E3]" />
        <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t("dash.trendTitle")}</h3>
      </div>
      <p className="mb-3 text-[12.5px] text-[#8E8E93]">{single ? t("dash.trendDescSingle") : t("dash.trendDescMulti")}</p>
      <div className="mb-3 grid grid-cols-2 gap-3">
        {showSalary && (
        <div className="rounded-xl bg-[#0071E3]/[0.06] px-3 py-2">
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#0071E3]"><span className="h-2 w-2 rounded-full bg-[#0071E3]" /> {t("dash.salaryPaid")}</span>
          <p className="num mt-0.5 text-[16px] font-bold tracking-tight">Rp {rp(totGaji)}</p>
        </div>
        )}
        <div className={`rounded-xl bg-[#FF9500]/[0.08] px-3 py-2 ${showSalary ? "" : "col-span-2"}`}>
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#B26A00]"><span className="h-2 w-2 rounded-full bg-[#FF9500]" /> {t("dash.cashAdv")}</span>
          <p className="num mt-0.5 text-[16px] font-bold tracking-tight">Rp {rp(totKasbon)}</p>
        </div>
      </div>
      {showSalary && (
      <div className="mb-4 flex h-1.5 w-full overflow-hidden rounded-full bg-black/[0.04]">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${gajiPct}%`, background: "linear-gradient(90deg,#3B93FF,#0062CC)" }} />
        <div className="h-full flex-1 rounded-full transition-all duration-700" style={{ background: "linear-gradient(90deg,#FFB23E,#E8820E)" }} />
      </div>
      )}
      <div className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {single ? (
            <BarChart
              data={(showSalary ? [{ name: t("dash.salaryPaid"), value: data[0]?.total_gaji || 0, g: "url(#hdSalary)" }] : [])
                .concat([{ name: t("dash.cashAdv"), value: data[0]?.total_kasbon || 0, g: "url(#hdCash)" }])}
              margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
            >
              <defs>
                <linearGradient id="hdSalary" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B93FF" /><stop offset="100%" stopColor="#0062CC" /></linearGradient>
                <linearGradient id="hdCash" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFB23E" /><stop offset="100%" stopColor="#E8820E" /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#8E8E93" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} tickFormatter={rpShort} width={54} />
              <Tooltip formatter={(v) => [`Rp ${rp(v)}`, t("dash.amount")]} cursor={{ fill: "#00000006", radius: 8 }} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={90}>
                {(showSalary ? ["url(#hdSalary)", "url(#hdCash)"] : ["url(#hdCash)"]).map((c) => <Cell key={c} fill={c} />)}
              </Bar>
            </BarChart>
          ) : (
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="mfGaji" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B93FF" stopOpacity={0.4} /><stop offset="100%" stopColor="#3B93FF" stopOpacity={0} /></linearGradient>
                <linearGradient id="mfGajiLine" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#0062CC" /><stop offset="100%" stopColor="#3B93FF" /></linearGradient>
                <linearGradient id="mfKasbon" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFB23E" /><stop offset="100%" stopColor="#E8820E" /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8E8E93" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} tickFormatter={rpShort} width={54} />
              <Tooltip formatter={(v, nm) => [`Rp ${rp(v)}`, nm]} cursor={{ fill: "#00000006", radius: 8 }} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total_kasbon" name={t("dash.cashAdv")} radius={[6, 6, 0, 0]} fill="url(#mfKasbon)" maxBarSize={34} />
              <Area type="monotone" dataKey="total_gaji" name={t("dash.salaryPaid")} stroke="url(#mfGajiLine)" strokeWidth={3} fill="url(#mfGaji)" dot={{ r: 3, fill: "#0071E3", strokeWidth: 0 }} activeDot={{ r: 6 }} hide={!showSalary} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const KasbonPulse = ({ k, onDebtor }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const total = Math.max(0, Number(k.outstanding) || 0);
  const uang = Math.max(0, Number(k.uang) || 0);
  const barang = Math.max(0, Number(k.barang) || 0);
  const comp = uang + barang || 1;
  const uangPct = (uang / comp) * 100;
  const people = (k.with_debt || 0) + (k.clear || 0);
  const clearPct = people ? Math.round(((k.clear || 0) / people) * 100) : 100;
  const trend = k.trend || [];
  const latest = trend.length ? trend[trend.length - 1].total : 0;
  const avg = trend.length ? trend.reduce((a, b) => a + (b.total || 0), 0) / trend.length : 0;
  const momentum = avg > 0 ? Math.round(((latest - avg) / avg) * 100) : 0;
  const debtors = (k.debtors || []).slice(0, 6);

  const R = 30;
  const CIRC = 2 * Math.PI * R;

  return (
    <div data-testid="home-kasbon-overview" className="liquid-glass-card overflow-hidden">
      {/* header band */}
      <div className="flex items-center gap-2.5 border-b border-black/[0.06] bg-gradient-to-r from-[#FF3B30]/[0.07] via-transparent to-transparent px-5 py-4">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#FF3B30]/[0.12] text-[#FF3B30]">
          <Wallet className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <h3 className="text-[14.5px] font-semibold tracking-tight sm:text-[16px]">{t("kasbon.title")}</h3>
          <p className="text-[11.5px] text-[#8E8E93]">{t("kasbon.pulseSub")}</p>
        </div>
        <button
          data-testid="home-kasbon-open"
          onClick={() => navigate("/kasbon")}
          className="ml-auto flex items-center gap-1 rounded-full bg-black/[0.04] px-3 py-1.5 text-[12px] font-medium text-[#0071E3] transition-colors hover:bg-[#0071E3]/10"
        >
          {t("dash.openPage")} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-12">
        {/* HERO — outstanding + clear ring */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-5">
            <div className="min-w-0">
              <p className="label text-[#C0362C]">{t("kasbon.stat.total")}</p>
              <p className="num mt-1 break-words text-[30px] font-bold leading-none tracking-tight text-[#C0362C] sm:text-[38px]">
                Rp {rp(total)}
              </p>
              <p className="mt-2 text-[12px] text-[#8E8E93]">
                <span className="font-semibold text-[#1D1D1F]">{k.with_debt || 0}</span> {t("kasbon.pulseDebtorsSuffix")}
              </p>
            </div>
            <div className="relative shrink-0">
              <svg width="78" height="78" viewBox="0 0 78 78" className="-rotate-90">
                <defs>
                  <linearGradient id="kpRing" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#34C759" />
                    <stop offset="100%" stopColor="#0E8A46" />
                  </linearGradient>
                </defs>
                <circle cx="39" cy="39" r={R} fill="none" stroke="#0000000D" strokeWidth="8" />
                <circle
                  cx="39" cy="39" r={R} fill="none" stroke="url(#kpRing)" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - clearPct / 100)}
                  style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="num text-[16px] font-bold leading-none text-[#0E8A46]">{clearPct}%</span>
                <span className="mt-0.5 text-[9.5px] font-medium uppercase tracking-wide text-[#8E8E93]">{t("kasbon.pulseClear")}</span>
              </div>
            </div>
          </div>

          {/* composition bar */}
          <div className="mt-5">
            <div className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-black/[0.04]">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${uangPct}%`, background: "linear-gradient(90deg,#3B93FF,#0062CC)" }} />
              <div className="h-full flex-1 rounded-full transition-all duration-700" style={{ background: "linear-gradient(90deg,#FF6B61,#C0362C)" }} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-[#0071E3]/[0.06] px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#0071E3]">
                  <span className="h-2 w-2 rounded-full bg-[#0071E3]" /> {t("kasbon.bonUang")}
                </span>
                <p className="num mt-0.5 text-[15px] font-bold tracking-tight">Rp {rp(uang)}</p>
              </div>
              <div className="rounded-xl bg-[#C0362C]/[0.06] px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#C0362C]">
                  <span className="h-2 w-2 rounded-full bg-[#C0362C]" /> {t("kasbon.bonBarang")}
                </span>
                <p className="num mt-0.5 text-[15px] font-bold tracking-tight">Rp {rp(barang)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* MOMENTUM — repayments area chart */}
        <div className="lg:col-span-7">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#128C3E]" />
              <h4 className="text-[13px] font-semibold tracking-tight">{t("kasbon.pulseRepay6")}</h4>
            </div>
            {trend.length > 1 && (
              <span className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${momentum >= 0 ? "bg-[#E9F9EF] text-[#0E8A46]" : "bg-[#FDECEC] text-[#C0362C]"}`}>
                {momentum >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(momentum)}% <span className="font-normal opacity-70">{t("kasbon.pulseVsAvg")}</span>
              </span>
            )}
          </div>
          {trend.length === 0 ? (
            <div className="grid h-[150px] place-items-center text-[12.5px] text-[#8E8E93]">{t("kasbon.pulseNoRepay")}</div>
          ) : (
            <div className="h-[150px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 8, right: 6, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="kpArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34C759" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#34C759" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="kpStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#0E8A46" />
                      <stop offset="100%" stopColor="#34C759" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#8E8E93" }} tickLine={false} axisLine={false} width={42} tickFormatter={rpShort} />
                  <Tooltip formatter={(v) => [`Rp ${rp(v)}`, t("kasbon.paid")]} cursor={{ stroke: "#128C3E", strokeWidth: 1, strokeDasharray: "4 4" }} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
                  <Area type="monotone" dataKey="total" stroke="url(#kpStroke)" strokeWidth={2.5} fill="url(#kpArea)" dot={{ r: 2.5, fill: "#0E8A46", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* top debtors strip */}
      {debtors.length > 0 && (
        <div className="border-t border-black/[0.06] px-5 py-3.5">
          <p className="label mb-2.5">{t("kasbon.pulseTopDebtors")}</p>
          <div className="flex gap-2.5 overflow-x-auto pb-1">
            {debtors.map((d) => (
              <button
                key={d.employee_id}
                data-testid={`home-kasbon-debtor-${d.employee_id}`}
                onClick={() => onDebtor(d)}
                className="group flex shrink-0 items-center gap-2.5 rounded-2xl border border-black/[0.05] bg-white/70 py-1.5 pl-1.5 pr-3.5 transition-all hover:border-[#FF3B30]/30 hover:shadow-sm active:scale-[0.98]"
              >
                <EmployeeAvatar id={d.employee_id} nama={d.nama} hasPhoto={d.has_photo} size={34} />
                <div className="text-left">
                  <p className="flex items-center text-[12.5px] font-medium leading-tight">
                    <span className="max-w-[110px] truncate">{d.nama}</span>
                    {d.verified && <VerifiedBadge />}
                  </p>
                  <p className="num text-[12px] font-bold leading-tight text-[#C0362C]">Rp {rp(d.balance)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function HomeDashboard() {
  const navigate = useNavigate();
  const { user, readOnly } = useAuth();
  const { t } = useT();
  const greetName = (user?.name || user?.username || "").trim().split(/\s+/)[0] || "there";
  const salaryHidden = user?.role === "jarit";
  const [meEmp, setMeEmp] = useState(null);
  useEffect(() => { api.get("/me/employee").then((r) => setMeEmp(r.data)).catch(() => setMeEmp(null)); }, []);
  const [data, setData] = useState(null);
  const [available, setAvailable] = useState([]);
  const [preset, setPreset] = useState("12");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emp, setEmp] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [kstats, setKstats] = useState(null);
  const rangeRef = useRef({ start: "", end: "" });

  const load = useCallback(async (silent) => {
    const { start: s, end: e } = rangeRef.current;
    if (silent) setRefreshing(true);
    try {
      const { data } = await api.get("/dashboard/analytics", { params: s && e ? { start: s, end: e } : {} });
      setData(data);
      if (data.available?.length) setAvailable(data.available);
      const act = await api.get("/activities", { params: { limit: 20 } }).then((r) => r.data).catch(() => []);
      setActivities(act);
      return data;
    } catch (err) {
      if (!silent) toast.error(errMsg(err));
      return null;
    } finally {
      if (silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const d = await load(false);
      const av = d?.available || [];
      if (av.length) {
        // Default to the last 1 year (up to 12 available periods).
        const n = Math.min(12, av.length);
        setStart(av[av.length - n].value);
        setEnd(av[av.length - 1].value);
      }
    })();
  }, [load]);

  useEffect(() => {
    rangeRef.current = { start, end };
    if (start && end) load(false);
  }, [start, end, load]);

  useEffect(() => {
    const iv = setInterval(() => load(true), 20000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    api.get("/kasbon/stats").then((r) => setKstats(r.data)).catch(() => {});
  }, []);

  const applyPreset = (p) => {
    setPreset(p);
    if (!available.length) return;
    const n = Math.min(Number(p), available.length);
    setStart(available[available.length - n].value);
    setEnd(available[available.length - 1].value);
  };
  const onStart = (v) => { setPreset("custom"); setStart(v); };
  const onEnd = (v) => { setPreset("custom"); setEnd(v); };

  const downloadXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/dashboard/xlsx", { params: { start, end }, responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = m ? m[1] : "Dashboard.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setExporting(false);
    }
  };

  const openEmp = async (r) => {
    setEmp({ nama: r.nama, verified: r.verified, rows: null });
    setEmpLoading(true);
    try {
      const { data } = await api.get(`/dashboard/employee/${r.employee_id}`, { params: { start, end } });
      setEmp({ ...data, verified: r.verified });
    } catch (err) {
      toast.error(errMsg(err));
      setEmp(null);
    } finally {
      setEmpLoading(false);
    }
  };

  const openItem = async (r) => {
    const nb = r.nama_barang || r.nama;
    if (!nb) return;
    setItem({ nama_barang: nb });
    setItemData(null);
    try {
      const { data } = await api.get("/dashboard/item-producers", { params: { nama_barang: nb, start, end } });
      setItemData(data);
    } catch (err) {
      toast.error(errMsg(err));
      setItemData({ producers: [], total: 0 });
    }
  };

  const s = data?.summary || {};
  const prev = data?.prev_summary || null;
  const pd = data?.period_days || 0;
  const trend = data?.trend || [];

  return (
    <div data-testid={TID.home.root} className="space-y-6">
      <div className="flex items-center gap-3">
        {user?.has_photo ? (
          <AccountAvatar id={user.id} name={greetName} hasPhoto size={52} />
        ) : meEmp?.has_photo ? (
          <EmployeeAvatar id={meEmp.id} nama={greetName} hasPhoto size={52} />
        ) : (
          <AccountAvatar id={user?.id} name={greetName} hasPhoto={false} size={52} />
        )}
        <h1 className="flex items-center gap-1 text-[22px] font-semibold tracking-tight sm:text-[30px]">
          {t("dash.welcome", { name: titleCase(greetName) })}
          {user?.verified && <VerifiedBadge className="h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]" />}
        </h1>
      </div>

      <LiveAttendance />

      <div className="flex flex-col gap-3 border-t border-black/[0.06] pt-6 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <h2 className="text-[18px] font-semibold tracking-tight sm:text-[22px]">{t("dash.overview")}</h2>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex w-full rounded-full bg-black/[0.04] p-1 sm:w-auto">
            {PRESETS.map((p) => (
              <button
                key={p.k}
                data-testid={TID.home.preset(p.k)}
                onClick={() => applyPreset(p.k)}
                className={`flex-1 rounded-full px-3 py-1.5 text-center text-[12.5px] font-medium transition-all sm:flex-none ${
                  preset === p.k ? "bg-white text-[#0071E3] shadow-sm" : "text-[#6E6E73] hover:text-[#1D1D1F]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Select value={start} onValueChange={onStart}>
              <SelectTrigger data-testid={TID.home.start} className="h-9 min-w-0 flex-1 rounded-full border-[#E1E1E6] bg-white text-[13px] sm:w-[160px] sm:flex-none">
                <SelectValue placeholder="From" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {available.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[13px] text-[#8E8E93]">–</span>
            <Select value={end} onValueChange={onEnd}>
              <SelectTrigger data-testid={TID.home.end} className="h-9 min-w-0 flex-1 rounded-full border-[#E1E1E6] bg-white text-[13px] sm:w-[160px] sm:flex-none">
                <SelectValue placeholder="To" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {available.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!salaryHidden && (
            <button
              data-testid={TID.home.export}
              onClick={downloadXlsx}
              disabled={exporting || !start}
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("dash.export")}
            </button>
            )}
          </div>
        </div>
      </div>

      {!data ? (
        <div className="grid place-items-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {!salaryHidden && <StatCard testid={TID.home.cardGaji} icon={Banknote} label={t("dash.statSalary")} value={`Rp ${rp(s.total_gaji)}`} accent="#0071E3" cur={s.total_gaji} prev={prev?.total_gaji} />}
            <StatCard testid={TID.home.cardKaryawan} icon={Users} label={t("dash.statActive")} value={s.total_karyawan_aktif} accent="#34C759" cur={s.total_karyawan_aktif} prev={prev?.total_karyawan_aktif} />
            <StatCard testid={TID.home.cardHari} icon={CalendarDays} label={t("dash.statWorkDays", { n: pd })} value={s.total_hari} accent="#FF9500" cur={s.total_hari} prev={prev?.total_hari} />
            <StatCard testid={TID.home.cardBarang} icon={Scissors} label={t("dash.statJarit")} value={rp(s.total_barang_jarit)} accent="#AF52DE" cur={s.total_barang_jarit} prev={prev?.total_barang_jarit} />
          </div>

          {!salaryHidden && <ApprovalsSummaryCard />}

          {kstats && <KasbonPulse k={kstats} onDebtor={openEmp} />}

          <DeviceHealthCompact />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <AttendanceRateChart trend={trend} />
            </div>
            <div className="lg:col-span-7">
              <MonthlyFlowChart trend={trend} hideSalary={salaryHidden} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <RankList testid={TID.home.topAttendance} title={t("dash.topAtt")} icon={CalendarDays}
              rows={data.top_attendance} valueOf={(r) => r.hari} fmt={(v) => v} denom={pd} unit={` ${t("dash.days")}`} accent="#FF9500" onSelect={openEmp} />
            <RankList testid={TID.home.topJarit} title={t("dash.topTailors")} icon={Scissors}
              rows={data.top_jarit} valueOf={(r) => r.jumlah} fmt={(v) => rp(v)} unit={` ${t("dash.pcs")}`} accent="#AF52DE" onSelect={openEmp} />
            <RankList testid={TID.home.topBarang} title={t("dash.mostItems")} icon={ClipboardList}
              rows={data.top_barang} valueOf={(r) => r.jumlah} fmt={(v) => rp(v)} unit={` ${t("dash.pcs")}`} accent="#0071E3" onSelect={openItem} itemMode />
            <RankList testid={TID.home.topKasbon} title={t("dash.largestKasbon")} icon={Wallet}
              rows={kstats?.debtors || []} valueOf={(r) => r.balance} fmt={(v) => `Rp ${rp(v)}`} accent="#FF3B30" onSelect={openEmp} />
          </div>

          <AttendanceDashCards navigate={navigate} />

          {/* activity table */}
          <div data-testid="home-activity" className="liquid-glass-card overflow-hidden">
            <div className="flex items-center gap-2 px-5 pb-3 pt-5">
              <Activity className="h-4 w-4 text-[#0071E3]" />
              <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t("dash.recentActivity")}</h3>
              <span className="ml-1 flex items-center gap-1 rounded-full bg-[#E9F9EF] px-2 py-0.5 text-[10.5px] font-medium text-[#128C3E]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" /> Live
              </span>
            </div>
            {activities.length === 0 ? (
              <p className="py-10 text-center text-[13.5px] text-[#8E8E93]">No activity yet</p>
            ) : (
              <>
              {/* Mobile: stacked cards */}
              <div className="space-y-2.5 px-4 pb-4 md:hidden">
                {activities.map((a) => {
                  const m = ACT_META[a.kind] || ACT_META.edit;
                  const Icon = m.Icon;
                  return (
                    <div key={a.id} data-testid="home-activity-card" className="rounded-2xl border border-black/[0.05] bg-white/70 p-3.5">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full" style={{ background: m.bg, color: m.color }}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13.5px] leading-snug text-[#1D1D1F]">{a.message}</p>
                          <div className="mt-1.5 flex items-center justify-between gap-2">
                            <span className="inline-flex min-w-0 items-center text-[12px] font-medium text-[#6E6E73]">
                              <span className="truncate">{titleCase(a.actor)}</span>
                            </span>
                            <span className="shrink-0 text-[11.5px] text-[#8E8E93]">{timeAgo(a.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[460px] text-left">
                  <thead>
                    <tr className="border-y border-black/[0.06] text-[10.5px] uppercase tracking-wider text-[#A1A1A6]">
                      <th className="py-2.5 pl-5 pr-3 font-semibold">Activity</th>
                      <th className="px-3 py-2.5 font-semibold">By</th>
                      <th className="px-3 py-2.5 pr-5 text-right font-semibold">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((a) => {
                      const m = ACT_META[a.kind] || ACT_META.edit;
                      const Icon = m.Icon;
                      return (
                        <tr key={a.id} className="border-b border-black/[0.04] transition-colors last:border-0 hover:bg-white/50">
                          <td className="py-3 pl-5 pr-3">
                            <div className="flex items-start gap-3">
                              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full" style={{ background: m.bg, color: m.color }}>
                                <Icon className="h-4 w-4" />
                              </span>
                              <span className="text-[13px] leading-snug text-[#1D1D1F]">{a.message}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3">
                            <span className="inline-flex items-center text-[12.5px] font-medium text-[#6E6E73]">
                              {titleCase(a.actor)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 pr-5 text-right text-[12px] text-[#8E8E93]">{timeAgo(a.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          <div className="pt-2">
            <h3 className="mb-3 text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t("dash.openPage")}</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {NAV.map((n) => (
                <button
                  key={n.k}
                  data-testid={TID.home.nav(n.k)}
                  onClick={() => navigate(n.to)}
                  className="liquid-glass-card group flex items-center gap-3 p-4 text-left transition-all hover:shadow-md active:scale-[0.99]"
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0071E3]/[0.08] text-[#0071E3]">
                    <n.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold">{t(n.lblKey)}</p>
                    <p className="truncate text-[12px] text-[#8E8E93]">{t(n.dKey)}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={!!emp} onOpenChange={(v) => !v && setEmp(null)}>
        <DialogContent data-testid={TID.home.empDialog} className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-1.5 text-[19px] font-semibold tracking-tight">
              <span className="truncate">{emp?.nama}</span>
              {emp?.verified && !emp?.is_jarit && <VerifiedBadge className="h-[18px] w-[18px] shrink-0" />}
              <JaritBadge show={!empLoading && emp?.is_jarit} size={20} />
            </DialogTitle>
            {emp?.jabatan ? <p className="text-[13px] text-[#8E8E93]">{emp.jabatan}</p> : null}
          </DialogHeader>
          <div className="mt-4">
            {empLoading || !emp?.rows ? (
              <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13.5px]">
                  <thead>
                    <tr className="border-b border-black/[0.06] text-[11.5px] uppercase tracking-wide text-[#8E8E93]">
                      <th className="py-2 pr-2 text-left">Month</th>
                      <th className="py-2 px-2 text-right">Days</th>
                      {emp.is_jarit && <th className="py-2 px-2 text-right">Items</th>}
                      {!salaryHidden && <th className="py-2 px-2 text-right">Salary</th>}
                      <th className="py-2 pl-2 text-right">Kasbon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emp.rows.map((r) => (
                      <tr key={r.label} className="border-b border-black/[0.04] last:border-0">
                        <td className="py-2 pr-2 font-medium">{r.label}</td>
                        <td className="num py-2 px-2 text-right">{r.present ? r.hari : "—"}</td>
                        {emp.is_jarit && <td className="num py-2 px-2 text-right">{r.present ? rp(r.jumlah) : "—"}</td>}
                        {!salaryHidden && <td className="num py-2 px-2 text-right">{r.present ? `Rp ${rp(r.gaji)}` : "—"}</td>}
                        <td className="num py-2 pl-2 text-right text-[#FF3B30]">{r.kasbon ? `Rp ${rp(r.kasbon)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!item} onOpenChange={(v) => { if (!v) { setItem(null); setItemData(null); } }}>
        <DialogContent data-testid="home-item-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[480px]">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 text-[18px] font-semibold tracking-tight capitalize">
              <Package className="h-4 w-4 text-[#0071E3]" /> {item?.nama_barang}
            </DialogTitle>
            <p className="text-[12.5px] text-[#8E8E93]">Who produced this · {itemData?.label || ""}</p>
          </DialogHeader>
          {!itemData ? (
            <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
          ) : itemData.producers.length === 0 ? (
            <p className="py-10 text-center text-[13.5px] text-[#8E8E93]">No production of this item in range.</p>
          ) : (
            <>
              <div className="mt-2 flex items-baseline justify-between rounded-xl bg-[#F5F5F7] px-3.5 py-2.5">
                <span className="text-[12.5px] font-medium text-[#6E6E73]">Total produced</span>
                <span className="num text-[15px] font-bold text-[#0071E3]">{rp(itemData.total)} <span className="text-[11px] font-normal text-[#8E8E93]">pcs</span></span>
              </div>
              <ol className="mt-3 space-y-2.5">
                {itemData.producers.map((p, i) => {
                  const im = Math.max(1, ...itemData.producers.map((x) => x.jumlah));
                  return (
                    <li key={p.employee_id || i}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[13.5px] font-medium">{p.nama}</span>
                        <span className="num shrink-0 text-[13px] font-semibold">{rp(p.jumlah)} <span className="text-[11px] font-normal text-[#8E8E93]">pcs</span></span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/[0.05]">
                        <div className="h-full rounded-full bg-[#0071E3]" style={{ width: `${(p.jumlah / im) * 100}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const todayMks = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

const AttendanceDashCards = ({ navigate }) => {
  const { t } = useT();
  const [today] = useState(todayMks);
  const [issues, setIssues] = useState(null);
  const [eod, setEod] = useState(null);

  const loadCards = useCallback(async () => {
    try {
      const [ex, ed] = await Promise.all([
        api.get("/attendance/exceptions", { params: { date: today, status: "Open" } }),
        api.get("/attendance/end-of-day", { params: { date: today } }),
      ]);
      setIssues(ex.data);
      setEod(ed.data);
    } catch {
      /* silent */
    }
  }, [today]);

  useEffect(() => {
    loadCards();
    const t = setInterval(loadCards, 30000);
    return () => clearInterval(t);
  }, [loadCards]);

  const open = issues?.summary?.open ?? 0;
  const s = eod?.summary || {};
  const complete = (s.full_day ?? 0) + (s.half_day ?? 0);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <button
        data-testid="home-attendance-issues-card"
        title="Actionable attendance exceptions requiring admin attention."
        onClick={() => navigate(`/attendance/fingerprint?date=${today}&status=OPEN_ISSUES`)}
        className="liquid-glass-card group flex items-center justify-between gap-3 p-4 text-left transition-all hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex items-center gap-3">
          <span className={`grid h-11 w-11 place-items-center rounded-xl ${open ? "bg-[#FFF1E6] text-[#B5590F]" : "bg-[#E9F9EF] text-[#0E8A46]"}`}>
            {open ? <AlertTriangle className="h-5 w-5" /> : <Check className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <p className="num text-[20px] font-bold leading-none">{open}</p>
            <p className="mt-1 text-[12.5px] font-medium text-[#1D1D1F]">{t("dash.openIssues")}</p>
            <p className="mt-0.5 text-[11px] text-[#8E8E93]">{t("dash.openIssuesDesc")}</p>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
      </button>
      <button
        data-testid="home-attendance-review-card"
        onClick={() => navigate(`/attendance/end-of-day?date=${today}`)}
        className="liquid-glass-card group flex items-center justify-between gap-3 p-4 text-left transition-all hover:shadow-md active:scale-[0.99]"
      >
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EAF0FF] text-[#0058C7]">
            <ClipboardList className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[13.5px] font-semibold leading-tight">{t("dash.reviewStatus")}</p>
            <p className="num mt-1 text-[12.5px] text-[#8E8E93]">
              {complete} {t("dash.complete")} · {s.needs_review ?? 0} {t("dash.needReview")}
            </p>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  );
};
